import { useEffect, useRef } from "react";

export const UNSAVED_CHANGES_MESSAGE = "You have unsaved changes. Leave without saving?";

// The in-app link a click is about to follow, or null if it stays on this page / leaves the app.
export const navigationLink = (
  event: MouseEvent,
  here: Location = window.location
): { anchor: HTMLAnchorElement; url: URL } | null => {
  if (event.defaultPrevented || event.button !== 0) return null;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
  // composedPath reaches anchors inside shadow DOM (prism-menu and prism-button render their own <a>).
  const anchor = event
    .composedPath()
    .find((el): el is HTMLAnchorElement => el instanceof HTMLAnchorElement && !!el.getAttribute("href"));
  if (!anchor || anchor.hasAttribute("download")) return null;
  if (anchor.target && anchor.target !== "_self") return null;
  const url = new URL(anchor.href, here.href);
  if (url.origin !== here.origin) return null;
  if (url.pathname === here.pathname && url.search === here.search) return null;
  return { anchor, url };
};

export const navigationTarget = (event: MouseEvent, here: Location = window.location): URL | null =>
  navigationLink(event, here)?.url ?? null;

/**
 * Asks before throwing away unsaved edits.
 *
 * - In-app links (header, Admin Settings menu): a capture-phase click listener runs before
 *   React Router's <Link> or Prism's menu handler and holds the click while `confirm` (an in-app
 *   dialog, see useConfirm) asks. Leaving replays the click on the same link, so whichever handler
 *   owned it navigates exactly as it would have.
 * - Reload, closing the tab and leaving the site: browsers only allow their own beforeunload
 *   prompt here; no page can replace it.
 *
 * The app uses a declarative <BrowserRouter>, so React Router's useBlocker is unavailable and
 * the browser's Back button is not intercepted.
 */
const useUnsavedChangesGuard = (
  dirty: boolean,
  confirm: (message: string) => Promise<boolean>,
  message = UNSAVED_CHANGES_MESSAGE
) => {
  // Kept in a ref so a new confirm function identity doesn't re-register the listeners.
  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;

  useEffect(() => {
    if (!dirty) return;
    let replaying = false;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Older browsers need returnValue set to show the prompt.
      event.returnValue = "";
    };

    const onClick = (event: MouseEvent) => {
      if (replaying) return;
      const link = navigationLink(event);
      if (!link) return;
      event.preventDefault();
      event.stopPropagation();
      confirmRef.current(message).then((leave) => {
        if (!leave) return;
        window.removeEventListener("beforeunload", onBeforeUnload);
        if (link.anchor.isConnected) {
          replaying = true;
          try {
            link.anchor.click();
          } finally {
            replaying = false;
          }
        } else {
          window.location.assign(link.url.href);
        }
      });
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [dirty, message]);
};

export default useUnsavedChangesGuard;
